import { Box, Button, Center, HStack, IconButton } from "@chakra-ui/react";
import { ArrowBigDown, ArrowBigUp } from "lucide-react";
import React, { Children, PropsWithChildren, useState } from "react";
import { SimpleTooltip } from "../SimpleTooltip";
import { DisplayMode } from "../Post";
import { HeadingFractionState } from "./Heading/HeadingFraction";
import { ImageFractionState } from "./Image/ImageFraction";
import { LinkFractionState } from "./Link/LinkFraction";
import { TextState } from "./Text/TextEditor/TextFraction";
import { AliasType } from "../../PostFlow";
import { QueryFractionState } from "./Query/QueryFraction";

export type FractionState =
  | TextState
  | ImageFractionState
  | LinkFractionState
  | HeadingFractionState
  | QueryFractionState
  | undefined;

export type FractionType =
  | "text"
  | "image"
  | "file"
  | "heading"
  | "links"
  | "query";

export type FractionChildMode = Extract<
  DisplayMode,
  DisplayMode.DISPLAY | DisplayMode.EDIT
>;

export interface FractionProps<T> {
  state?: T;
  alias?: AliasType;
  onStateChange: (state: T) => void;
  mode: DisplayMode;
  onModeChange: (mode: DisplayMode) => void;
  onDelete: () => void;
  moveUp: () => void;
  moveDown: () => void;
}

export interface FractionChildProps<T> {
  state?: T;
  mode?: FractionChildMode;
  onChange?: (state: T) => void;
  alias?: AliasType;
}

export function PostFraction<T>(props: PropsWithChildren<FractionProps<T>>) {
  const child = Children.only(props.children) as React.ReactElement;

  const adjustedChild = React.cloneElement(child, {
    ...child.props,
    state: props.state,
    alias: props.alias,
    mode: props.mode,
    onChange: (newState: T) => {
      props.onStateChange(newState);
    },
  });

  const [editButtonStyle, setEditButtonStyle] = useState<{ display: string }>({
    display: "none",
  });
  const [previewDivStyle, setPreviewDivStyle] = useState<
    | { background: string | undefined }
    | {
        background: string;
        borderWidth: string;
        borderColor: string;
      }
  >({
    background: "white",
  });

  if (props.mode === DisplayMode.DISPLAY_EDITABLE) {
    return (
      <div
        className="base_editor2"
        style={previewDivStyle}
        onMouseEnter={(e) => {
          setPreviewDivStyle({
            background: "#daf6fc",
            borderWidth: "2px",
            borderColor: "gray",
          });
          setEditButtonStyle({ display: "flex" });
        }}
        onMouseLeave={(e) => {
          setPreviewDivStyle({ background: undefined });
          setEditButtonStyle({ display: "none" });
        }}
      >
        <SimpleTooltip>
          {adjustedChild}
          <HStack>
            <IconButton
              aria-label={"Up"}
              icon={<ArrowBigUp />}
              style={editButtonStyle}
              size={"sm"}
              colorScheme="gray"
              textColor={"black"}
              opacity={"90%"}
              onClick={() => props.moveUp()}
            />
            <IconButton
              aria-label={"Down"}
              icon={<ArrowBigDown />}
              style={editButtonStyle}
              size={"sm"}
              colorScheme="gray"
              textColor={"black"}
              opacity={"90%"}
              onClick={() => props.moveDown()}
            />
            <Box flex="1" />
            <Button
              size={"xs"}
              style={editButtonStyle}
              colorScheme="yellow"
              borderColor={"blue.700"}
              borderWidth={"2px"}
              opacity={"75%"}
              onClick={() => {
                props.onModeChange(DisplayMode.EDIT);
              }}
            >
              EDIT
            </Button>
            <Button
              size={"xs"}
              style={editButtonStyle}
              colorScheme="red"
              borderRadius={"40px"}
              borderColor={"blue.700"}
              borderWidth={"2px"}
              opacity={"90%"}
              onClick={() => {
                props.onDelete();
              }}
            >
              DELETE
            </Button>
          </HStack>
        </SimpleTooltip>
      </div>
    );
  } else if (props.mode === DisplayMode.DISPLAY) {
    return <div>{adjustedChild}</div>;
  } else if (props.mode === DisplayMode.EDIT) {
    return (
      <div
        style={{
          background: "#eee",
          borderWidth: "2px",
          borderColor: "gray",
        }}
      >
        <div
          style={{
            background: "#daf6fc",
            borderWidth: "1px",
            borderStyle: "dotted",
            borderColor: "gray",
          }}
        >
          <div
            className="base_editor"
            style={{
              margin: "20px",
              marginTop: "20px",
              borderColor: "#999999",
              borderRadius: "10px",
              borderWidth: "1px",
              background: "white",
            }}
          >
            {adjustedChild}
          </div>
        </div>
        <Center>
          <Button
            onClick={() => {
              props.onModeChange(DisplayMode.DISPLAY_EDITABLE);

              // Also remove the blue "focus" styling when edit is dismissed
              setPreviewDivStyle({ background: "white" });
              setEditButtonStyle({ display: "none" });
            }}
            colorScheme="green"
            width={"lg"}
            marginTop={"20px"}
            marginBottom={"10px"}
          >
            OK
          </Button>
        </Center>
      </div>
    );
  }
  return null;
}

const Test = (props: FractionChildProps<string>) => {
  return (
    <div
      onClick={() => {
        if (props.mode === DisplayMode.EDIT) {
          props.onChange &&
            props.onChange(props.state === "FOO" ? "BAR" : "FOO");
        }
      }}
    >
      {"state: " + props.state + " mode:" + props.mode}
    </div>
  );
};

export const TestFraction = (props: FractionProps<string>) => {
  return (
    <PostFraction {...props}>
      <Test />
    </PostFraction>
  );
};
